import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Cookies from "js-cookie";
import LogoPostperty from "../assets/assets/logoPostperty.png";

const ModernNavbar = ({ forceSolid = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(forceSolid);
  
  // Auth check - prioritize JWT cookie then local token
  const token = Cookies.get("jwt") || localStorage.getItem("token");

  useEffect(() => {
    if (forceSolid) return;
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [forceSolid]);

  const handleImageError = (e, fallback = "/favicon.ico") => {
    e.target.src = fallback;
    e.target.onerror = null;
  };

  const menuLinks = [
    { label: "หน้าหลัก", href: "/home" },
    { label: "ติดต่อเรา", href: "/contact" },
    { label: "ทรัพย์ที่ถูกใจ", href: "/cart" },
    { label: "โปรไฟล์", href: "/profiles" },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 px-6 lg:px-12 py-4 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm py-3' : 'bg-transparent py-6'}`}>
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.location.replace("/")}>
            <img 
              src={LogoPostperty} 
              className="w-8 h-8 lg:w-12 lg:h-12 object-contain" 
              alt="PostPerty Logo" 
              onError={(e) => handleImageError(e)}
            />
            <div className="flex flex-col">
              <span className={`text-lg lg:text-2xl font-black tracking-tighter leading-none uppercase ${scrolled ? 'text-slate-900' : 'text-white'}`}>POSTPERTY</span>
            </div>
          </div>

          <div className={`hidden lg:flex items-center gap-10 text-sm font-bold ${scrolled ? 'text-slate-600' : 'text-white'}`}>
            {menuLinks.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-[#34bfd9] transition-colors relative">
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {!token ? (
              <>
                <button 
                  onClick={() => window.location.replace("/login")}
                  className={`hidden sm:block px-6 py-2 rounded-full text-xs font-black transition-all border-2 ${scrolled ? 'border-slate-200 text-slate-900 hover:bg-slate-50' : 'border-white/30 text-white hover:bg-white/10'}`}
                >
                  เข้าสู่ระบบ
                </button>
                <button
                  onClick={() => window.location.replace("/register")}
                  className={`px-5 py-2 lg:px-8 lg:py-3 rounded-full text-xs lg:text-sm font-black transition-all ${scrolled ? 'bg-black text-white shadow-lg' : 'bg-white text-black shadow-xl hover:scale-105 active:scale-95'}`}
                >
                  ลงทะเบียน
                </button>
              </>
            ) : (
              <button 
                onClick={() => window.location.replace("/profiles")}
                className={`px-5 py-2 lg:px-8 lg:py-3 rounded-full text-xs lg:text-sm font-black transition-all ${scrolled ? 'bg-black text-white shadow-lg' : 'bg-white text-black shadow-xl hover:scale-105 active:scale-95'}`}
              >
                จัดการโปรไฟล์
              </button>
            )}
            <button className={`lg:hidden ${scrolled ? 'text-slate-900' : 'text-white'}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
               <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }} 
            animate={{ opacity: 1, x: 0 }} 
            exit={{ opacity: 0, x: '100%' }} 
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[200] bg-white flex flex-col shadow-2xl"
          >
            <div className="flex justify-between items-center p-6 border-b border-slate-50">
              <div className="flex items-center gap-2">
                <img src={LogoPostperty} className="w-10 h-10 object-contain" alt="" />
                <span className="text-xl font-black tracking-tighter text-slate-900">POSTPERTY</span>
              </div>
              <button onClick={() => setIsMenuOpen(false)} className="p-3 bg-slate-50 rounded-full text-slate-400 hover:text-slate-900 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 flex flex-col justify-center px-10 gap-8">
              {menuLinks.map((link, i) => (
                <motion.a
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-4xl font-black text-slate-900 tracking-tighter hover:text-[#34bfd9] transition-colors"
                >
                  {link.label}
                </motion.a>
              ))}
            </div>

            <div className="p-10 border-t border-slate-50">
              {!token ? (
                <button 
                  onClick={() => window.location.replace("/login")} 
                  className="w-full py-5 bg-black text-white rounded-2xl font-black text-lg shadow-xl active:scale-95 transition-transform"
                >
                  เข้าสู่ระบบ
                </button>
              ) : (
                <button 
                  onClick={() => window.location.replace("/profiles")} 
                  className="w-full py-5 bg-black text-white rounded-2xl font-black text-lg shadow-xl active:scale-95 transition-transform"
                >
                  จัดการโปรไฟล์
                </button>
              )}
              <p className="text-center text-slate-400 text-xs mt-6 font-bold tracking-widest uppercase">© 2026 POSTPERTY</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ModernNavbar;
